/** Sets as the app writes them (SetSummary, RaceTime, Format.weight). */
import { recordOf, unitSuffix } from "./plan.js";
import type { Exercise, SetLog } from "./types.js";
import { LB, shownUnit, shownWeight, unitChoice } from "../prefs.js";

export { LB };

export function weightNumber(value: number): string {
  let text = Number(value).toFixed(2);
  while (text.endsWith("0")) text = text.slice(0, -1);
  if (text.endsWith(".")) text = text.slice(0, -1);
  return text;
}

function nearlyInteger(x: number): boolean {
  return Math.abs(x - Math.round(x)) < 1e-9;
}

export function raceTime(seconds: number, withUnit = true): string {
  const h = Math.round(seconds * 100) / 100;
  const decimals = nearlyInteger(h * 10) ? 1 : 2;
  if (h < 60) {
    const number = nearlyInteger(h) ? String(Math.round(h)) : h.toFixed(decimals);
    return withUnit ? number + " s" : number;
  }
  const minutes = Math.floor(h / 60);
  let rest = (h - minutes * 60).toFixed(decimals);
  while (rest.length < decimals + 3) rest = "0" + rest;
  return minutes + ":" + rest;
}

export function isDistance(log: SetLog): boolean {
  return log.measure === "meters";
}

export function kilograms(log: SetLog): number | null {
  return log.weight == null ? null : log.weight * (log.unit === "lb" ? LB : 1);
}

export function setText(log: SetLog, exercise?: Exercise | null): string {
  if (log.skipped) return "Skipped";
  if (exercise && recordOf(exercise) === "done") return "Done";
  if (isDistance(log) && log.amount != null) return runText(log, exercise);
  const unit = exercise?.target?.unit || log.measure || "reps";
  if (log.weight == null && log.amount == null) return "Done";
  const load = log.weight != null ? weightNumber(shownWeight(log.weight, log.unit)) + " " + shownUnit(log.unit) : "BW";
  if (log.amount == null) return load;
  return load + " × " + log.amount + unitSuffix(unit);
}

/** A rep of two or more runs (§19.1, §19.2): by its exercise's parts, or by the times it took. */
export function isInParts(log: SetLog, exercise?: Pick<Exercise, "parts"> | null): boolean {
  return (exercise?.parts?.length ?? 0) > 1 || (log.partTimes?.length ?? 0) > 1;
}

/** Hundredths as the 1080 shows them, one decimal kept: 7.94, 7.9, 8.0. */
export function speedNumber(speed: number): string {
  const text = speed.toFixed(2);
  return text.endsWith("0") ? text.slice(0, -1) : text;
}

/** A distance rep as the phone writes it: `20 m · 6 kg · 3.41 s · 7.9 m/s`, missing parts left out.
 * A rep in parts: `150 m + 100 m · 24.1 s + 16.2 s`, `–` for a part not timed. */
export function runText(log: SetLog, exercise?: Pick<Exercise, "parts"> | null): string {
  const metres = exercise?.parts && exercise.parts.length > 1 ? exercise.parts : null;
  const parts = [metres ? metres.map((m) => m + " m").join(" + ") : log.amount + " m"];
  if (log.weight != null && log.weight > 0) parts.push(weightNumber(log.weight) + " " + (log.unit || "kg"));
  const partTimes = log.partTimes && log.partTimes.length > 1 ? log.partTimes : null;
  if (partTimes && partTimes.some((t) => t != null)) parts.push(partTimes.map((t) => (t != null ? raceTime(t) : "–")).join(" + "));
  else if (log.time != null) parts.push(raceTime(log.time));
  if (log.topSpeed != null && log.topSpeed > 0) parts.push(speedNumber(log.topSpeed) + " m/s");
  return parts.join(" · ");
}

export function isBetter(a: SetLog, b: SetLog): boolean {
  if (isDistance(a) || isDistance(b)) return (a.time ?? Infinity) < (b.time ?? Infinity);
  const x = kilograms(a) || 0;
  const y = kilograms(b) || 0;
  if (x !== y) return x > y;
  return (a.amount || 0) > (b.amount || 0);
}

/** The best set; a skipped one is never it (§61.8). */
export function topSet<T extends SetLog>(logs: T[]): T | null {
  let best: T | null = null;
  for (const log of logs) if (!log.skipped && (!best || isBetter(log, best))) best = log;
  return best;
}

/** A personal best's words: the phone's label, unless weights show in another unit, then the set itself. */
export function recordText(label: string, log: SetLog, exercise?: Exercise | null): string {
  return unitChoice() === "logged" ? label : setText(log, exercise);
}
